import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Box } from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import type { EmblaCarouselType } from "embla-carousel";
import {
  ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT,
  ECOSYSTEM_CORRIDOR_EXPLORE_CARD_WIDTH,
} from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";

const AUTOPLAY_DELAY_MS = 4500;
const CARD_SLIDE_SIZE = `${ECOSYSTEM_CORRIDOR_EXPLORE_CARD_WIDTH}px`;

type EcosystemExploreCardCarouselProps = {
  children: ReactNode;
  /** Number of carousel pages (after chunking), used for controls/autoplay. */
  itemCount: number;
};

/** Auto-rotating carousel; fixed-width slides stretch to the tallest card. */
export default function EcosystemExploreCardCarousel({
  children,
  itemCount,
}: EcosystemExploreCardCarouselProps) {
  const [embla, setEmbla] = useState<EmblaCarouselType | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const multi = itemCount > 1;

  useEffect(() => {
    if (!embla || !multi) return;

    const advance = () => {
      if (pausedRef.current) return;
      if (embla.canScrollNext()) {
        embla.scrollNext();
      } else {
        embla.scrollTo(0);
      }
    };

    const id = window.setInterval(advance, AUTOPLAY_DELAY_MS);
    return () => window.clearInterval(id);
  }, [embla, multi]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const syncSlideHeights = () => {
      const slides = Array.from(root.querySelectorAll<HTMLElement>(".mantine-Carousel-slide"));
      if (slides.length === 0) return;

      for (const slide of slides) {
        slide.style.height = "auto";
        slide.style.minHeight = `${ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT}px`;
      }

      let tallest = ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT;
      for (const slide of slides) {
        tallest = Math.max(tallest, slide.getBoundingClientRect().height);
      }

      const heightPx = `${Math.ceil(tallest)}px`;
      for (const slide of slides) {
        slide.style.minHeight = heightPx;
        slide.style.height = heightPx;
      }
    };

    const schedule = () => requestAnimationFrame(syncSlideHeights);
    syncSlideHeights();
    const observer = new ResizeObserver(schedule);
    observer.observe(root);
    return () => observer.disconnect();
  }, [children, itemCount]);

  return (
    <Box ref={rootRef} w="100%">
      <Carousel
        getEmblaApi={setEmbla}
        slideSize={CARD_SLIDE_SIZE}
        slideGap="md"
        align="start"
        slidesToScroll={1}
        withControls={multi}
        controlsOffset={0}
        loop={multi}
        onMouseEnter={() => {
          pausedRef.current = true;
        }}
        onMouseLeave={() => {
          pausedRef.current = false;
        }}
        styles={{
          root: { width: "100%" },
          container: { alignItems: "stretch" },
          slide: {
            minHeight: ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT,
            height: "auto",
            padding: 0,
            display: "flex",
            flexDirection: "column",
          },
          control: {
            backgroundColor: "#FFFFFF",
            border: "1px solid #E2E8F0",
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.1)",
          },
        }}
      >
        {children}
      </Carousel>
    </Box>
  );
}

export function EcosystemExploreCarouselSlide({ children }: { children: ReactNode }) {
  return (
    <Carousel.Slide style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}>
        {children}
      </Box>
    </Carousel.Slide>
  );
}
